// Gap detection (ARCHITECTURE.md §11.3 step 1, §11.4).
//
// Deterministic, LLM-free analysis that finds file pairs which *should* be
// related but lack a deterministic edge in the AST Graph. Each gap is the
// targeted question the Semantic Analyzer later poses to the LLM. Gap detection
// uses contract-level information (the Navigation Index) and artifact membership
// to focus the search, so the LLM is never asked about unrelated file pairs.

import type { AstGraph } from "../ast/types.js";
import type { GraphEdge } from "../types.js";
import type { GapCandidate, GapReason } from "./types.js";

export interface DetectGapsOptions {
  astGraph: AstGraph;
  /** filePath → artifact IDs the file participates in. */
  artifactMapping: Map<string, string[]>;
  /** agent-contracts Navigation Index, when available. */
  navigationIndex?: Record<string, unknown>;
}

// Priority order: a stronger signal wins when the same pair is detected by
// multiple heuristics. (`unresolved_dynamic_import` is keyed independently.)
const REASON_PRIORITY: Record<GapReason, number> = {
  contract_related_no_edge: 3,
  same_artifact_no_edge: 2,
  naming_convention_match: 1,
  unresolved_dynamic_import: 0,
};

// Generic barrel / entry-point names carry no relationship signal — every
// module has an `index`, so matching on them produces only noise.
const GENERIC_STEMS = new Set(["index", "mod", "main", "lib"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Stable key for an unordered file pair. */
function pairKey(a: string, b: string): string {
  return a < b ? `${a} ${b}` : `${b} ${a}`;
}

/** Build the set of file pairs already linked by a deterministic edge. */
function deterministicPairs(edges: GraphEdge[]): Set<string> {
  const pairs = new Set<string>();
  for (const edge of edges) {
    if (edge.classification === "deterministic") {
      pairs.add(pairKey(edge.from, edge.to));
    }
  }
  return pairs;
}

/**
 * Normalize a file path to a "stem" for naming-convention matching: the base
 * name with its extension and common role suffixes (test/spec/gen/...) removed.
 * `src/user.ts` and `tests/user.test.ts` both normalize to `user`.
 */
function namingStem(filePath: string): string {
  const base = filePath.split("/").pop() ?? filePath;
  // Drop the final extension, then strip a trailing role suffix segment.
  const noExt = base.replace(/\.[^.]+$/, "");
  return noExt
    .replace(/[.-](test|spec|gen|generated|d|stories|mock|mocks)$/i, "")
    .toLowerCase();
}

/**
 * Extract related artifact pairs from a Navigation Index. Reads both
 * `source_artifacts` and `derived_artifacts` relations on each artifact node.
 */
function extractContractRelations(
  navigationIndex: Record<string, unknown> | undefined,
): { fromArtifact: string; toArtifact: string; relation: string }[] {
  const relations: {
    fromArtifact: string;
    toArtifact: string;
    relation: string;
  }[] = [];
  const artifacts = navigationIndex?.["artifacts"];
  if (!isRecord(artifacts)) return relations;

  for (const [id, node] of Object.entries(artifacts)) {
    if (!isRecord(node)) continue;

    const sources = node["source_artifacts"];
    if (Array.isArray(sources)) {
      for (const src of sources) {
        if (typeof src === "string") {
          // `id` derives from `src`: the source is the origin of the relation.
          relations.push({
            fromArtifact: src,
            toArtifact: id,
            relation: "source_artifacts",
          });
        }
      }
    }

    const derived = node["derived_artifacts"];
    if (Array.isArray(derived)) {
      for (const d of derived) {
        if (typeof d === "string") {
          relations.push({
            fromArtifact: id,
            toArtifact: d,
            relation: "derived_artifacts",
          });
        }
      }
    }
  }
  return relations;
}

/** Files belonging to `artifactId`, derived from the artifact mapping. */
function filesForArtifact(
  artifactMapping: Map<string, string[]>,
  artifactId: string,
): string[] {
  const files: string[] = [];
  for (const [path, ids] of artifactMapping) {
    if (ids.includes(artifactId)) files.push(path);
  }
  return files;
}

/**
 * Detect file pairs that should be related but lack a deterministic edge.
 *
 * Heuristics, in descending priority:
 *  - `contract_related_no_edge`: files in artifacts the contract graph relates
 *  - `same_artifact_no_edge`: files sharing an artifact
 *  - `naming_convention_match`: files whose names share a normalized stem
 *  - `unresolved_dynamic_import`: dynamic imports with a non-literal target
 */
export function detectGaps(options: DetectGapsOptions): GapCandidate[] {
  const { astGraph, artifactMapping, navigationIndex } = options;

  const linked = deterministicPairs(astGraph.edges);
  const fileNodes = new Set<string>();
  for (const node of astGraph.nodes.values()) {
    if (node.kind === "file") fileNodes.add(node.id);
  }

  // Keep the highest-priority reason per unordered pair.
  const byPair = new Map<string, GapCandidate>();
  const consider = (candidate: GapCandidate): void => {
    if (candidate.fileA === candidate.fileB) return;
    if (linked.has(pairKey(candidate.fileA, candidate.fileB))) return;
    const key = pairKey(candidate.fileA, candidate.fileB);
    const existing = byPair.get(key);
    if (
      existing === undefined ||
      REASON_PRIORITY[candidate.gapReason] > REASON_PRIORITY[existing.gapReason]
    ) {
      byPair.set(key, candidate);
    }
  };

  // 1. contract_related_no_edge — files across contract-related artifacts.
  for (const rel of extractContractRelations(navigationIndex)) {
    const fromFiles = filesForArtifact(artifactMapping, rel.fromArtifact);
    const toFiles = filesForArtifact(artifactMapping, rel.toArtifact);
    for (const a of fromFiles) {
      for (const b of toFiles) {
        consider({
          fileA: a,
          fileB: b,
          gapReason: "contract_related_no_edge",
          contractRelation: rel,
        });
      }
    }
  }

  // 2. same_artifact_no_edge — files sharing an artifact.
  const artifactFiles = new Map<string, string[]>();
  for (const [path, ids] of artifactMapping) {
    for (const id of ids) {
      const list = artifactFiles.get(id) ?? [];
      list.push(path);
      artifactFiles.set(id, list);
    }
  }
  for (const files of artifactFiles.values()) {
    for (let i = 0; i < files.length; i++) {
      for (let j = i + 1; j < files.length; j++) {
        consider({
          fileA: files[i]!,
          fileB: files[j]!,
          gapReason: "same_artifact_no_edge",
        });
      }
    }
  }

  // 3. naming_convention_match — files whose names share a normalized stem.
  const byStem = new Map<string, string[]>();
  for (const path of fileNodes) {
    const stem = namingStem(path);
    if (stem.length === 0 || GENERIC_STEMS.has(stem)) continue;
    const list = byStem.get(stem) ?? [];
    list.push(path);
    byStem.set(stem, list);
  }
  for (const files of byStem.values()) {
    if (files.length < 2) continue;
    for (let i = 0; i < files.length; i++) {
      for (let j = i + 1; j < files.length; j++) {
        consider({
          fileA: files[i]!,
          fileB: files[j]!,
          gapReason: "naming_convention_match",
        });
      }
    }
  }

  const gaps = [...byPair.values()];

  // 4. unresolved_dynamic_import — dynamic imports to a non-file target. These
  //    are not file-pair heuristics, so they bypass the pair dedup map.
  const seenDynamic = new Set<string>();
  for (const edge of astGraph.edges) {
    const isDynamic = edge.evidence.some((e) => e.type === "dynamic_import");
    if (!isDynamic) continue;
    if (fileNodes.has(edge.to)) continue; // resolved to a real file already
    const key = `${edge.from} ${edge.to}`;
    if (seenDynamic.has(key)) continue;
    seenDynamic.add(key);
    gaps.push({
      fileA: edge.from,
      fileB: edge.to,
      gapReason: "unresolved_dynamic_import",
    });
  }

  return gaps;
}
